import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Switch,
  Card,
  CardBody,
  Divider,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../contexts/ApiContext';
import {
  CordeCommonModelsDtoUserUser as UserDto,
  CordeCommonModelsDtoUserUpdateUserRequest as UpdateUserRequest,
} from '../../../api/generated/v1/api';
import { UserProfile } from '../../../contexts/userprofile/UserProfileContext';

interface AccountSettingsProps {
  userId: string;
  profile: UserProfile | null;
  onMarkChanged: (hasChanges: boolean) => void;
  onClearChanges: () => void;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({
  userId,
  profile,
  onMarkChanged,
  onClearChanges,
}) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const api = useApi();
  const toast = useToast();

  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    phone: '',
  });
  const [originalData, setOriginalData] = useState({
    email: '',
    username: '',
    phone: '',
  });

  // Load user data
  useEffect(() => {
    loadUserData();
  }, [userId]);

  // Check for changes
  useEffect(() => {
    const hasChanges = Object.keys(formData).some(
      key => formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
    );
    onMarkChanged(hasChanges);
  }, [formData, originalData, onMarkChanged]);

  const loadUserData = async () => {
    setIsLoading(true);
    try {
      const response = await api.UserApi.getUser(userId);
      const userData = response.data;
      setUser(userData);
      
      const data = {
        email: userData.email || '',
        username: userData.username || '',
        phone: userData.phone || '',
      };
      
      setFormData(data);
      setOriginalData(data);
    } catch (error: any) {
      console.error('Failed to load user data:', error);
      toast({
        title: t('settings.account.loadError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const updateRequest: UpdateUserRequest = {
        email: formData.email,
        username: formData.username,
        phone: formData.phone,
      };

      await api.UserApi.updateUserData(userId, updateRequest);
      
      // Update original data
      setOriginalData(formData);
      onClearChanges();
      
      toast({
        title: t('settings.account.saveSuccess'),
        description: t('settings.account.saveSuccessMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Reload user data
      await loadUserData();
    } catch (error: any) {
      console.error('Failed to save user data:', error);
      toast({
        title: t('settings.account.saveError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  if (isLoading) {
    return (
      <VStack spacing={4}>
        <Spinner size="lg" />
        <Text>{t('common.loading')}</Text>
      </VStack>
    );
  }

  if (!user) {
    return (
      <Alert status="error">
        <AlertIcon />
        {t('settings.account.userNotFound')}
      </Alert>
    );
  }

  const hasChanges = Object.keys(formData).some(
    key => formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <Heading size="md">{t('settings.account.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('settings.account.description')}
        </Text>
      </VStack>

      {/* Basic Information */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.account.basicInfo')}</Heading>
            
            <FormControl>
              <FormLabel>{t('settings.account.email')}</FormLabel>
              <Input
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('settings.account.emailPlaceholder')}
                type="email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('settings.account.username')}</FormLabel>
              <Input
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder={t('settings.account.usernamePlaceholder')}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('settings.account.phone')}</FormLabel>
              <Input
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder={t('settings.account.phonePlaceholder')}
                type="tel"
              />
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Account Status */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.account.status')}</Heading>
            
            <HStack justify="space-between">
              <VStack align="flex-start" spacing={1}>
                <Text fontWeight="medium">{t('settings.account.emailVerification')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.account.emailVerificationDescription')}
                </Text>
              </VStack>
              <Badge
                colorScheme={user.email_verified ? 'green' : 'red'}
                size="lg"
                px={3}
                py={1}
                borderRadius="full"
              >
                {user.email_verified 
                  ? t('settings.account.verified')
                  : t('settings.account.notVerified')
                }
              </Badge>
            </HStack>

            <HStack justify="space-between">
              <VStack align="flex-start" spacing={1}>
                <Text fontWeight="medium">{t('settings.account.phoneVerification')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.account.phoneVerificationDescription')}
                </Text>
              </VStack>
              <Badge
                colorScheme={user.phone_verified ? 'green' : 'red'}
                size="lg"
                px={3}
                py={1}
                borderRadius="full"
              >
                {user.phone_verified 
                  ? t('settings.account.verified')
                  : t('settings.account.notVerified')
                }
              </Badge>
            </HStack>

            <HStack justify="space-between">
              <VStack align="flex-start" spacing={1}>
                <Text fontWeight="medium">{t('settings.account.twoFactor')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.account.twoFactorDescription')}
                </Text>
              </VStack>
              <Badge
                colorScheme={user.two_factor_enabled ? 'green' : 'gray'}
                size="lg"
                px={3}
                py={1}
                borderRadius="full"
              >
                {user.two_factor_enabled 
                  ? t('settings.account.enabled')
                  : t('settings.account.disabled')
                }
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Save Actions */}
      {hasChanges && (
        <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
          <CardBody>
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSubmitting}
              >
                {t('common.reset')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={t('common.saving')}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 